import React, { createContext, useContext, useState, ReactNode } from 'react';
import { CalendarEvent, EventType } from '../../types/calendar';
import { logger } from '../../services/logger';

const log = logger.createScope('AppointmentDialogContext');

interface AppointmentDialogContextType {
  open: boolean;
  openDialog: (date?: Date, eventType?: EventType, initialData?: Partial<CalendarEvent>) => void;
  closeDialog: () => void;
  selectedDate: Date | null;
  eventType: EventType | null;
  initialEventData: Partial<CalendarEvent> | undefined;
  onCloseCallback?: () => void;
  setOnCloseCallback: (callback: () => void) => void;
}

const AppointmentDialogContext = createContext<AppointmentDialogContextType | undefined>(undefined);

export const useAppointmentDialog = () => {
  const ctx = useContext(AppointmentDialogContext);
  if (!ctx) throw new Error('useAppointmentDialog must be used within AppointmentDialogProvider');
  return ctx;
};

export const AppointmentDialogProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [eventType, setEventType] = useState<EventType | null>(null);
  const [initialEventData, setInitialEventData] = useState<Partial<CalendarEvent> | undefined>(undefined);
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | undefined>(undefined);
  
  const openDialog = (date?: Date, type?: EventType, initialData?: Partial<CalendarEvent>) => {
    log.debug('openDialog called', {
      date: date?.toISOString(),
      type,
      initialData: initialData?.id || 'no-id',
      hasId: !!initialData?.id,
      fullInitialData: initialData
    });
    setSelectedDate(date || null);
    setEventType(type || null);
    setInitialEventData(initialData || undefined);
    setOpen(true);
  };
  
  const closeDialog = () => {
    setOpen(false);
    setEventType(null);
    setInitialEventData(undefined);
    // Execute the callback if it exists
    if (onCloseCallback) {
      onCloseCallback();
      // Reset the callback after execution
      setOnCloseCallback(undefined);
    }
  };
  
  return (
    <AppointmentDialogContext.Provider value={{ 
      open, 
      openDialog, 
      closeDialog, 
      selectedDate,
      eventType,
      initialEventData,
      onCloseCallback,
      setOnCloseCallback
    }}>
      {children}
    </AppointmentDialogContext.Provider>
  );
}; 